import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  IconButton,
  Button,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import GridViewIcon from '@mui/icons-material/GridView';
import LinearScaleIcon from '@mui/icons-material/LinearScale';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import LayersIcon from '@mui/icons-material/Layers';
import InboxIcon from '@mui/icons-material/Inbox';
import AddIcon from '@mui/icons-material/Add';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import type { LocationTreeNode } from '../pages/WmsLocationManager';
import { locationService, CreateLocationDto } from '../services/locationService';
import BulkCreateModal from './BulkCreateModal';
import { useTranslation } from 'react-i18next';
import { buildDefaultLocationName, formatLocationDisplayName, getLocationTypeLabel } from '../shared/locationLabels';

const TYPE_ICONS: Record<string, React.ReactNode> = {
  Zone: <GridViewIcon fontSize="small" sx={{ color: '#1565c0' }} />,
  Aisle: <LinearScaleIcon fontSize="small" sx={{ color: '#6a1b9a' }} />,
  Platform: <ViewModuleIcon fontSize="small" sx={{ color: '#2e7d32' }} />,
  Rack: <ViewColumnIcon fontSize="small" sx={{ color: '#e65100' }} />,
  Level: <LayersIcon fontSize="small" sx={{ color: '#00838f' }} />,
  Bin: <InboxIcon fontSize="small" sx={{ color: '#4e342e' }} />,
};

const CHILD_TYPE_MAP: Record<string, string[]> = {
  Zone: ['Aisle', 'Platform'],
  Aisle: ['Platform'],
  Platform: ['Rack'],
  Rack: ['Rack', 'Level'],
  Level: ['Bin'],
};

interface LocationTreeProps {
  treeData: LocationTreeNode[];
  warehouseId: string;
  selectedNodeId: string | null;
  occupiedLocationIds?: Set<string>;
  onSelect: (id: string) => void;
  onDataChanged: () => void;
  showSnackbar: (msg: string, sev?: 'success' | 'error') => void;
}

const LocationTree: React.FC<LocationTreeProps> = ({
  treeData,
  warehouseId,
  selectedNodeId,
  occupiedLocationIds,
  onSelect,
  onDataChanged,
  showSnackbar,
}) => {
  const { t } = useTranslation('warehouse');

  // Alt ağacında (kendisi veya torunları) stok bulunan düğümleri TEK geçişte işaretle.
  // Böylece ürün olan raf belirgin gösterilir ve üst zincir (koridor/peron) de kalınlaşır,
  // kullanıcı stoğun yerini ağaçta hızlıca bulur.
  const subtreeOccupied = useMemo(() => {
    const set = new Set<string>();
    const visit = (node: LocationTreeNode): boolean => {
      let has = !!occupiedLocationIds?.has(node.Id);
      for (const child of node.Children) {
        if (visit(child)) has = true;
      }
      if (has) set.add(node.Id);
      return has;
    };
    treeData.forEach(visit);
    return set;
  }, [treeData, occupiedLocationIds]);

  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [addDialog, setAddDialog] = useState<{ open: boolean; parentId: string | null; parentType: string; parentCode: string } | null>(null);
  const [bulkModal, setBulkModal] = useState<{ open: boolean; parentId: string; parentCode: string } | null>(null);

  // Seçilen düğümün üst zincirini otomatik genişlet (kolon raf → dikey raflar görünsün)
  useEffect(() => {
    if (!selectedNodeId) return;
    const findAncestors = (
      nodes: LocationTreeNode[],
      targetId: string,
      trail: string[] = [],
    ): string[] | null => {
      for (const n of nodes) {
        if (n.Id === targetId) return trail;
        const found = findAncestors(n.Children, targetId, [...trail, n.Id]);
        if (found) return found;
      }
      return null;
    };
    const ancestors = findAncestors(treeData, selectedNodeId);
    if (ancestors && ancestors.length > 0) {
      setExpanded(prev => {
        const next = new Set(prev);
        ancestors.forEach(id => next.add(id));
        return next;
      });
    }
  }, [selectedNodeId, treeData]);

  const [newCode, setNewCode] = useState('');
  const [newName, setNewName] = useState('');
  const [newType, setNewType] = useState('');
  const [addLoading, setAddLoading] = useState(false);

  const toggleExpand = (id: string) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const countSiblingsOfType = (type: string, parentId: string | null) => {
    if (!parentId) return treeData.filter(n => n.Type === type).length;
    const findParent = (nodes: LocationTreeNode[]): LocationTreeNode | null => {
      for (const n of nodes) {
        if (n.Id === parentId) return n;
        const found = findParent(n.Children);
        if (found) return found;
      }
      return null;
    };
    const parent = findParent(treeData);
    return parent?.Children.filter(c => c.Type === type).length ?? 0;
  };

  const openAddDialog = (parentId: string | null, parentType: string, parentCode: string) => {
    const childTypes = parentId ? (CHILD_TYPE_MAP[parentType] || []) : ['Zone'];
    const childType = childTypes[0] || 'Zone';
    const nextIndex = countSiblingsOfType(childType, parentId) + 1;
    setNewCode('');
    setNewName(buildDefaultLocationName(childType, nextIndex, t));
    setNewType(childType);
    setAddDialog({ open: true, parentId, parentType, parentCode });
  };

  const handleAdd = async () => {
    if (!newCode.trim() || !newName.trim() || !newType) return;
    try {
      setAddLoading(true);
      const dto: CreateLocationDto = {
        WarehouseId: warehouseId,
        ParentId: addDialog?.parentId || undefined,
        Code: newCode.trim(),
        Name: newName.trim(),
        Type: newType,
      };
      await locationService.create(dto);
      showSnackbar(t('components.locationTree.created', { type: getLocationTypeLabel(newType, t), name: newName }));
      setAddDialog(null);
      onDataChanged();
    } catch {
      showSnackbar(t('components.locationTree.createError'), 'error');
    } finally {
      setAddLoading(false);
    }
  };

  const allowedChildTypes = addDialog?.parentId
    ? (CHILD_TYPE_MAP[addDialog.parentType] || [])
    : ['Zone'];

  const canBulkCreate = (type: string) => type === 'Platform' || type === 'Rack';

  const renderNode = (node: LocationTreeNode, depth: number) => {
    const hasChildren = node.Children.length > 0;
    const isExpanded = expanded.has(node.Id);
    const isSelected = node.Id === selectedNodeId;
    const childTypes = CHILD_TYPE_MAP[node.Type] || [];
    const isRack = node.Type === 'Rack';
    const directlyOccupied = !!occupiedLocationIds?.has(node.Id);
    // Kolon rafın kendisinde stok olmayabilir; stok alt (raf-seviyesi) çocuklardadır.
    const hasStock = subtreeOccupied.has(node.Id);
    const rackColor = isRack ? (hasStock ? '#e65100' : '#2e7d32') : undefined;

    return (
      <Box key={node.Id}>
        <ListItemButton
          selected={isSelected}
          onClick={() => onSelect(node.Id)}
          sx={{
            pl: 1 + depth * 2,
            py: 0.5,
            minHeight: 36,
            borderRadius: 1,
            // Ürün olan rafı belirgin yap: koyu/kalın sol kenar + hafif dolgu.
            ...(directlyOccupied && {
              bgcolor: 'rgba(230, 81, 0, 0.14)',
              borderLeft: '4px solid #212121',
              '&:hover': { bgcolor: 'rgba(230, 81, 0, 0.22)' },
            }),
            // Alt ağacında stok olan üst düğümlere (koridor/peron/kolon) daha ince vurgu.
            ...(!directlyOccupied && hasStock && {
              borderLeft: '3px solid rgba(230, 81, 0, 0.55)',
            }),
          }}
        >
          {hasChildren ? (
            <IconButton
              size="small"
              onClick={(e) => { e.stopPropagation(); toggleExpand(node.Id); }}
              sx={{ mr: 0.5, p: 0.25 }}
            >
              {isExpanded ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
            </IconButton>
          ) : (
            <Box sx={{ width: 28, mr: 0.5 }} />
          )}
          <ListItemIcon sx={{ minWidth: 28 }}>
            {TYPE_ICONS[node.Type] || <InboxIcon fontSize="small" />}
          </ListItemIcon>
          <ListItemText
            primary={
              <Typography
                variant="body2"
                noWrap
                sx={{
                  fontWeight: directlyOccupied ? 700 : (hasStock || isSelected) ? 600 : 400,
                  fontSize: '0.8125rem',
                  color: directlyOccupied ? '#bf360c' : rackColor,
                }}
              >
                {formatLocationDisplayName(node.Name, node.Type, t)}
              </Typography>
            }
            secondary={
              <Typography
                variant="caption"
                color={isRack ? undefined : 'text.secondary'}
                noWrap
                sx={{ fontSize: '0.7rem', color: isRack ? rackColor : undefined }}
              >
                {node.Code} {node.SortOrder > 0 ? `(#${node.SortOrder})` : ''}
              </Typography>
            }
          />
        </ListItemButton>

        {/* unmountOnExit: kapalı alt ağaçlar DOM'a hiç basılmaz. Büyük depolarda (binlerce raf)
            tüm düğümlerin peşinen render edilmesi ağaç görünümünü kilitliyordu. */}
        <Collapse in={isExpanded} timeout="auto" unmountOnExit mountOnEnter>
          {isExpanded && node.Children.map(child => renderNode(child, depth + 1))}

          {/* Action buttons inside expanded node */}
          {childTypes.length > 0 && (
            <Box sx={{ pl: 3 + depth * 2, py: 0.25, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
              <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={(e) => { e.stopPropagation(); openAddDialog(node.Id, node.Type, node.Code); }}
                sx={{ fontSize: '0.7rem', textTransform: 'none' }}
              >
                {`${getLocationTypeLabel(childTypes[0], t)} ${t('components.locationTree.addSuffix')}`}
              </Button>
              {canBulkCreate(node.Type) && (
                <Tooltip title={t('components.locationTree.bulkCreateRack')}>
                  <Button
                    size="small"
                    startIcon={<PlaylistAddIcon />}
                    onClick={(e) => { e.stopPropagation(); setBulkModal({ open: true, parentId: node.Id, parentCode: node.Code }); }}
                    sx={{ fontSize: '0.7rem', textTransform: 'none' }}
                    color="secondary"
                  >
                    {t('components.locationTree.bulkAdd')}
                  </Button>
                </Tooltip>
              )}
            </Box>
          )}
        </Collapse>
      </Box>
    );
  };

  return (
    <Box>
      {/* Root-level add zone button */}
      <Box sx={{ mb: 1 }}>
        <Button
          size="small"
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={() => openAddDialog(null, '', '')}
          fullWidth
        >
          {t('components.locationTree.addZone')}
        </Button>
      </Box>

      {treeData.length === 0 ? (
        <Box sx={{ py: 4, textAlign: 'center' }}>
          <GridViewIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
          <Typography variant="body2" color="text.secondary">
            {t('components.locationTree.empty')}
          </Typography>
        </Box>
      ) : (
        <List dense disablePadding>
          {treeData.map(node => renderNode(node, 0))}
        </List>
      )}

      {/* Add single location dialog */}
      <Dialog open={!!addDialog?.open} onClose={() => setAddDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle>
          {addDialog?.parentId ? t('components.locationTree.addUnderParent', { parentCode: addDialog.parentCode }) : t('components.locationTree.addNewZone')}
        </DialogTitle>
        <DialogContent>
          {allowedChildTypes.length > 1 && (
            <FormControl fullWidth size="small" sx={{ mt: 1, mb: 2 }}>
              <InputLabel>{t('components.locationTree.typeLabel')}</InputLabel>
              <Select value={newType} label={t('components.locationTree.typeLabel')} onChange={e => setNewType(e.target.value)}>
                {allowedChildTypes.map(childType => (
                  <MenuItem key={childType} value={childType}>{getLocationTypeLabel(childType, t)}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <TextField
            autoFocus
            label={t('components.locationTree.codeLabel')}
            fullWidth
            value={newCode}
            onChange={e => setNewCode(e.target.value)}
            size="small"
            sx={{ mb: 2, mt: allowedChildTypes.length <= 1 ? 1 : 0 }}
          />
          <TextField
            label={t('components.locationTree.nameLabel')}
            fullWidth
            value={newName}
            onChange={e => setNewName(e.target.value)}
            size="small"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDialog(null)} disabled={addLoading}>{t('common.cancel')}</Button>
          <Button variant="contained" onClick={handleAdd} disabled={addLoading || !newCode.trim() || !newName.trim()}>
            {addLoading ? t('components.locationTree.adding') : t('components.locationTree.addSuffix')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk create modal */}
      {bulkModal && (
        <BulkCreateModal
          open={bulkModal.open}
          warehouseId={warehouseId}
          parentId={bulkModal.parentId}
          parentCode={bulkModal.parentCode}
          onClose={() => setBulkModal(null)}
          onCreated={() => { setBulkModal(null); onDataChanged(); }}
          showSnackbar={showSnackbar}
        />
      )}
    </Box>
  );
};

export default LocationTree;
